import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios'
import { createTracedHeaders } from '@/util/datadogRum'
import { Alert, Merchant, AlertFilter, AlertLevel } from '@/types/fcc'

// Concurrency throttle to mitigate backend rate limits
let currentConcurrentFCCRequests = 0
const maxConcurrentFCCRequests = 3
const fccRequestQueue: Array<() => void> = []

const acquireFCCSlot = async (): Promise<void> => {
  if (currentConcurrentFCCRequests < maxConcurrentFCCRequests) {
    currentConcurrentFCCRequests += 1
    return
  }
  await new Promise<void>(resolve => {
    fccRequestQueue.push(() => {
      currentConcurrentFCCRequests += 1
      resolve()
    })
  })
}

const releaseFCCSlot = (): void => {
  currentConcurrentFCCRequests = Math.max(0, currentConcurrentFCCRequests - 1)
  const next = fccRequestQueue.shift()
  if (next) next()
}

export const formatCurrency = (value: number): string => {
  if (typeof value !== 'number') {
    return String(value)
  }
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

export const getFCCData = async <T>(
  token: string,
  endpoint: string,
  method: 'get' | 'post' | 'put' | 'patch' | 'delete' = 'get',
  params: Record<string, unknown> = {},
  id: string | null = null,
  pagination: { page?: number; pageSize?: number } | null = null,
  additionalPath: string | null = null,
  maxRetries = 3,
  initialDelay = 1000,
): Promise<T> => {
  if (!token) {
    throw new Error('Missing access token for FCC request')
  }
  let url = endpoint
  if (id) {
    url = `${url}/${id}`
  }
  if (additionalPath) {
    url = `${url}/${additionalPath}`
  }

  // Inject DataDog trace headers for distributed tracing and error correlation.
  // FCCUtil creates its own Axios instance, so the global interceptor doesn't apply.
  const { headers: tracedHeaders } = createTracedHeaders({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  })

  const axiosInstance: AxiosInstance = axios.create({
    baseURL: url,
    headers: tracedHeaders,
  })

  const config: AxiosRequestConfig = {
    method: method.toLowerCase(),
    url: '',
  }

  if (['get', 'delete'].includes(config.method!)) {
    config.params = {
      ...params,
      ...(pagination && {
        page: pagination.page || 1,
        page_size: pagination.pageSize || 20,
      }),
    }
  } else if (['post', 'put', 'patch'].includes(config.method!)) {
    config.data = params
  }

  let lastError: Error
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      await acquireFCCSlot()
      const response = await axiosInstance.request(config)
      return response.data
    } catch (error) {
      lastError = error as Error

      // If this is an Axios error, decide whether it is retryable
      const axErr = error as AxiosError
      const isCanceled = axErr?.code === 'ERR_CANCELED'
      const status = axErr?.response?.status

      // Do not retry on cancellations, 403 (Forbidden - user authenticated but missing permissions), or other client errors (<500)
      const shouldRetry =
        !isCanceled && status !== 403 && (!status || status >= 500)

      if (shouldRetry && attempt < maxRetries) {
        const baseDelay = initialDelay * Math.pow(2, attempt)
        const jitter = Math.floor(Math.random() * Math.min(500, baseDelay))
        const delay = baseDelay + jitter
        await new Promise(resolve => setTimeout(resolve, delay))
        continue
      }
      break
    } finally {
      releaseFCCSlot()
    }
  }

  throw lastError!
}

export const filterAlert = (alert: Alert, filters: AlertFilter): boolean => {
  // Event type filter
  if (filters.eventType?.selected?.length > 0) {
    if (!filters.eventType.selected.includes(alert.EVENT_TYPE)) {
      return false
    }
  }

  // Alert level filter - use TOLERANCE as ALERT_LEVEL if ALERT_LEVEL is not available
  if (filters.alertLevel?.selected?.length > 0) {
    const alertLevel = alert.ALERT_LEVEL || (alert.TOLERANCE as AlertLevel)
    if (!filters.alertLevel.selected.includes(alertLevel)) {
      return false
    }
  }

  // Significance range filter
  if (
    alert.SIGNIFICANCE < filters.significance.min ||
    alert.SIGNIFICANCE > filters.significance.max
  ) {
    return false
  }

  // Search filter
  if (filters.search.value) {
    const searchTerm = filters.search.value.toLowerCase()
    const searchableFields = [
      alert.MERCHANT_ID,
      alert.MERCHANT_NAME,
      alert.MERCHANT_OWNER,
      alert.EVENT_TYPE,
      alert.DESCRIPTION,
    ].map(field => String(field).toLowerCase())

    if (!searchableFields.some(field => field.includes(searchTerm))) {
      return false
    }
  }

  return true
}

export const filterMerchant = (
  merchant: Merchant,
  filters: Record<string, unknown>,
): boolean => {
  // Owner filter
  if (
    filters.owner &&
    typeof filters.owner === 'object' &&
    'selected' in filters.owner
  ) {
    const ownerFilter = filters.owner as { selected: string[] }
    if (ownerFilter.selected.length > 0) {
      if (!ownerFilter.selected.includes(merchant.MERCHANT_OWNER)) {
        return false
      }
    }
  }

  // Platform filter
  if (
    filters.platform &&
    typeof filters.platform === 'object' &&
    'selected' in filters.platform
  ) {
    const platformFilter = filters.platform as { selected: string[] }
    if (platformFilter.selected.length > 0) {
      if (!platformFilter.selected.includes(merchant.PLATFORM)) {
        return false
      }
    }
  }

  // Tolerance filter
  if (
    filters.tolerance &&
    typeof filters.tolerance === 'object' &&
    'selected' in filters.tolerance
  ) {
    const toleranceFilter = filters.tolerance as { selected: string[] }
    if (toleranceFilter.selected.length > 0) {
      if (!toleranceFilter.selected.includes(merchant.TOLERANCE_ID)) {
        return false
      }
    }
  }

  // Search filter
  if (
    filters.search &&
    typeof filters.search === 'object' &&
    'value' in filters.search
  ) {
    const searchFilter = filters.search as { value: string }
    if (searchFilter.value) {
      const searchTerm = searchFilter.value.toLowerCase()
      const searchableFields = [
        merchant.MERCHANT_ID,
        merchant.MERCHANT_NAME,
        merchant.MERCHANT_OWNER,
        merchant.PLATFORM,
      ].map(field => String(field).toLowerCase())

      if (!searchableFields.some(field => field.includes(searchTerm))) {
        return false
      }
    }
  }

  return true
}

export const getAlertSeverityColor = (
  level: string,
): { textColor: string; backgroundColor: string } => {
  const colorMap: Record<
    string,
    { textColor: string; backgroundColor: string }
  > = {
    High: { textColor: 'text-surface-0 dark:text-surface-0', backgroundColor: 'bg-red-600 dark:bg-red-600' },
    Medium: { textColor: 'text-surface-0 dark:text-surface-0', backgroundColor: 'bg-orange-600 dark:bg-orange-600' },
    Low: { textColor: 'text-surface-0 dark:text-surface-0', backgroundColor: 'bg-green-600 dark:bg-green-600' },
  }
  return (
    colorMap[level] || {
      textColor: 'text-surface-0 dark:text-surface-0',
      backgroundColor: 'bg-surface-600 dark:bg-surface-600',
    }
  )
}

export const getToleranceColor = (
  tolerance: string,
): { textColor: string; backgroundColor: string; borderColor: string } => {
  switch (tolerance) {
    case 'High':
      return {
        textColor: 'text-green-700',
        backgroundColor: 'bg-green-50',
        borderColor: 'border-green-500/40',
      }
    case 'Medium':
      return {
        textColor: 'text-orange-700',
        backgroundColor: 'bg-orange-50',
        borderColor: 'border-orange-500/40',
      }
    case 'Low':
      return {
        textColor: 'text-red-700',
        backgroundColor: 'bg-red-50',
        borderColor: 'border-red-500/40',
      }
    default:
      return {
        textColor: 'text-surface-600 dark:text-surface-300',
        backgroundColor: 'bg-surface-100 dark:bg-surface-800',
        borderColor: 'border-surface-500/40 dark:border-surface-500/40',
      }
  }
}

// Style by monetary significance (liability impact). Higher dollars → more severe (redder).
export const getSignificanceColor = (
  significance: number,
): { textColor: string; backgroundColor: string } => {
  if (typeof significance !== 'number' || isNaN(significance)) {
    return { textColor: 'text-surface-0 dark:text-surface-0', backgroundColor: 'bg-surface-600 dark:bg-surface-600' }
  }

  // Thresholds can be tuned; start with sensible defaults
  if (significance >= 1_000_000) {
    return { textColor: 'text-red-800', backgroundColor: 'bg-transparent' }
  }
  if (significance >= 100_000) {
    return { textColor: 'text-red-800', backgroundColor: 'bg-transparent' }
  }
  if (significance >= 10_000) {
    return { textColor: 'text-orange-800', backgroundColor: 'bg-transparent' }
  }
  if (significance >= 1_000) {
    return { textColor: 'text-amber-800', backgroundColor: 'bg-transparent' }
  }
  if (significance > 0) {
    return { textColor: 'text-amber-700', backgroundColor: 'bg-transparent' }
  }

  // Zero or negative (no monetary liability impact)
  return { textColor: 'text-surface-800 dark:text-surface-200', backgroundColor: 'bg-transparent' }
}
